/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import * as font from '@constant/font';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as design from '@constant/design';
import { useTypedSelector } from '@hook/common/useStore';
import Gap from '@component/common/gap';
import TextBlock from 'component/common/block/text-block';
import ImageTextBlock from 'component/common/block/image-text-block';
import AuthBox from 'component/auth/auth-box';
import TextInput from '@component/common/input/text-input';
import { createAuthLogoImage } from '@util/image';
import { createErrorText } from '@util/string';
import IconBlock from '@component/common/block/icon-block';
import { useAuthenticationCode, useAuthInput, usePassword } from '@hook/auth/useAuth';
import { SERVICE } from '@config/environment';

const PasswordContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
`;

const FindPassword: React.FC = () => {
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) ?? null;
  const [email, setEmail] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const { code, username, password } = useAuthInput();
  const { repassword } = usePassword();
  const { authenticateCode, issueAuthenticationCode, codeState } = useAuthenticationCode();

  const handlePasswordSaveClick = async () => {
    const result = await repassword(username.value, password.value);
    if (!result) return;
    setIsCompleted(true);
  };
  const handleCodeSendButtonClick = async () => {
    const email = await issueAuthenticationCode({ service: SERVICE, username: username.value });
    if (!email) return;
    setEmail(email);
  };
  const handleCodeAuthenticateButtonClick = async () => await authenticateCode(email, code.value);

  return (
    <AuthBox design={design.AUTH_BOX_DESIGN}>
      <ImageTextBlock
        src={createAuthLogoImage()}
        alt='otterbrick'
        text='비밀번호 찾기'
        design={design.AUTH_LOGO_DESIGN}
      />
      <Gap height={size.MARGIN_2} />
      {codeState === 'init' && (
        <>
          <TextInput
            name={'username'}
            placeholder={'가입되어 있는 아이디'}
            design={design.AUTH_INPUT_DESIGN}
            onChange={username.handler}
            value={username.value}
          />
          <TextBlock
            text={createErrorText(errorCode)}
            design={{
              ...design.ERROR_TEXT_DESIGN,
              box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_1 },
            }}
          />
          <TextBlock
            text={'인증번호 보내기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeSendButtonClick, mode: 'throttle', delay: 3000 }}
          />
        </>
      )}
      {codeState === 'sent' && (
        <>
          <TextBlock
            design={{
              text: { size: 'medium' },
              box: { width: size.AUTH_BLOCK_WIDTH, height: size.AUTH_BLOCK_HEIGHT },
            }}
            text={email}
          />
          <TextInput
            name={'code'}
            placeholder={'인증번호'}
            design={{ ...design.AUTH_INPUT_DESIGN, box: { ...design.AUTH_INPUT_DESIGN.box, alignX: 'center' } }}
            onChange={code.handler}
            value={code.value}
          />
          <TextBlock
            text={createErrorText(errorCode)}
            design={{
              ...design.ERROR_TEXT_DESIGN,
              box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_1 },
            }}
          />
          <TextBlock
            text={'인증하기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeAuthenticateButtonClick, mode: 'throttle', delay: 3000 }}
          />
          <Gap height={size.MARGIN_1} />
          <TextBlock
            text={'인증번호 다시 보내기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeSendButtonClick, mode: 'throttle', delay: 3000 }}
          />
        </>
      )}
      {codeState === 'authenticated' &&
        (isCompleted ? (
          <TextBlock
            design={{
              text: { size: 'medium' },
              box: { width: size.AUTH_BLOCK_WIDTH, height: size.AUTH_BLOCK_HEIGHT },
            }}
            text='비밀번호가 재설정되었습니다.'
          />
        ) : (
          <>
            <PasswordContainer>
              <TextInput
                name='password'
                placeholder='비밀번호'
                design={design.PASSWORD_INPUT_DESIGN}
                onChange={password.handler}
                value={password.value}
                type={password.visible ? 'text' : 'password'}
              />
              <IconBlock
                icon={password.visible ? 'visibility-on' : 'visibility-off'}
                design={{
                  icon: { size: 'medium', kind: 'outlined', color: color.GRAY_1 },
                  box: { right: size.MARGIN_1, event: { cursor: 'pointer' } },
                }}
                click={{ handler: password.visibleHandler }}
              />
            </PasswordContainer>
            <TextBlock
              text={createErrorText(errorCode)}
              design={{
                ...design.ERROR_TEXT_DESIGN,
                box: {
                  ...design.ERROR_TEXT_DESIGN,
                  height: size.ERROR_TEXT_HEIGHT_2,
                  alignX: 'left',
                },
              }}
            />
            <TextBlock
              text='비밀번호 저장하기'
              design={design.createAuthButtonDesign()}
              click={{ handler: handlePasswordSaveClick }}
            />
          </>
        ))}
    </AuthBox>
  );
};

export default FindPassword;
